/**
 * useStaffProfile — shared access to the currently-logged-in user's CIMA
 * staff profile (name, role, etc.) resolved from /cima/auth/me.
 *
 * App.tsx already fetches /me once on login. This context just makes the
 * result reachable from any deep component without prop-drilling.
 */
import React, { createContext, useContext } from 'react';
import type { AuthMe } from '../api/auth';

const StaffProfileContext = createContext<AuthMe | null>(null);

export function StaffProfileProvider({
  me,
  children,
}: {
  me: AuthMe | null;
  children: React.ReactNode;
}) {
  return (
    <StaffProfileContext.Provider value={me}>
      {children}
    </StaffProfileContext.Provider>
  );
}

/** Returns the resolved CIMA staff profile, or null while loading / not signed in. */
export function useStaffProfile(): AuthMe | null {
  return useContext(StaffProfileContext);
}

/** Returns the user's first name from the staff record, or "there" as a polite fallback.
 *  Never returns the email address, even if no staff record is found. */
export function useFirstName(): string {
  const me = useContext(StaffProfileContext);
  if (me?.firstName) return me.firstName;
  if (me?.displayName) {
    // displayName falls back to email local-part on the server — strip dots/underscores
    // and capitalize so "yash.v" reads as "Yash" instead of an email handle.
    const first = me.displayName.split(/[.\s_-]/)[0];
    if (first) return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  return 'there';
}

/** Returns the user's full display name, or "" if not loaded. */
export function useDisplayName(): string {
  const me = useContext(StaffProfileContext);
  return me?.displayName || '';
}
